// Use jquery only for Ajax request
import $ from "jquery"
import AppConfig from "config/config.json"
import translationFR from 'front/translations/messages.fr.json';
import translationEN from 'front/translations/messages.en.json';
import translationDE from 'front/translations/messages.de.json';
import translationES from 'front/translations/messages.es.json';
import translationIT from 'front/translations/messages.it.json';
import translationDA from 'front/translations/messages.da.json';
import translationEL from 'front/translations/messages.el.json';
import translationFI from 'front/translations/messages.fi.json';
import translationNB from 'front/translations/messages.nb.json';
import translationNL from 'front/translations/messages.nl.json';
import translationPL from 'front/translations/messages.pl.json';
import translationPT from 'front/translations/messages.pt.json';
import translationSV from 'front/translations/messages.sv.json';
import {numberFragmentBeforeLive} from "../components/Player/Player";

export const PLAYER_USER_AGENT = 'InfomaniakPlayer';

/**
 * Ajoute le user agent du player dans les query params de l'url du stream
 * afin que le backend puisse identifier la provenance de la lecture
 * @param url
 * @returns {string} l'url avec le param ua, ou l'url inchangée si elle est relative/invalide
 */
export function addUserAgentToStreamUrl(url) {
    try {
        const urlWithUa = new URL(url);
        urlWithUa.searchParams.set('ua', PLAYER_USER_AGENT);
        return urlWithUa.toString();
    } catch (e) {
        return url;
    }
}

export function getTranslations(locale) {
    let translation;
    switch (locale) {
        case 'en':
            translation = translationEN;
            break;
        case 'de':
            translation = translationDE;
            break;
        case 'es':
            translation = translationES;
            break;
        case 'it':
            translation = translationIT;
            break;
        case 'da':
            translation = translationDA;
            break;
        case 'el':
            translation = translationEL;
            break;
        case 'fi':
            translation = translationFI;
            break;
        case 'nb':
            translation = translationNB;
            break;
        case 'nl':
            translation = translationNL;
            break;
        case 'pt':
            translation = translationPT;
            break;
        case 'pl':
            translation = translationPL;
            break;
        case 'sv':
            translation = translationSV;
            break;
        default:
            translation = translationFR;
    }
    return translation;
}

export function getConfigAsync(urlConfig) {
    return new Promise(resolve => {
        $.ajax({
            type: "GET",
            async: true,
            url: urlConfig,
            success: function (result) {
                resolve(result.data);
            }
        });
    });
}

export var getTimelineAsync = function (stationId) {
    // some metadata are used to trigger actions (ads for example) and should not be displayed in the timeline
    const excludedMetadata = ['#sc#2;1#sc#', '#sc#2;0#sc#', '#sc#', '-', '˗'];

    return new Promise(resolve => {
        $.ajax({
            type: "GET",
            async: true,
            url: AppConfig.urlInspector + '/' + stationId + '/metadata?covers=1',
            success: function (result) {
                resolve(result.data?.filter((track) => !excludedMetadata.includes(track.title.trim()))?.map((track) => {
                    const splitTitle = track.title.replace('˗', '-').split(' - ');

                    return {
                        ...track,
                        author: splitTitle.shift(),
                        title: splitTitle.join(' - '),
                    }
                }));
            }
        });
    });
}

export function getConfigUrl(props) {
    if (props.configId) {
        return AppConfig.urlConfig.replace('{player_uuid}', props.configId);
    } else if (props.templateName) {
        if (AppConfig.basename !== '' && AppConfig.env !== 'dev') {
            return `/${AppConfig.basename}/config-${props.templateName}.json`;
        } else {
            return `/config-${props.templateName}.json`;
        }
    }
}

export function isAuthorized(stream) {
    if (AppConfig.env === "dev") {
        return true;
    }

    // Default not authorized
    let result = false;
    $.ajax({
        type: "POST",
        async: false,
        url: AppConfig.urlAccess,
        data: {
            mountpoint: getMountpointFromStream(stream),
            urlParam: (new URL(stream.url)).search.substring(1).replaceAll('&', ',')
        },
        success: function (response) {
            result = response.data
        }
    });

    return result;
}

export function formatConfig(config, state, isPreview = false) {

    const locale = state.locale ? state.locale : config.language;
    const autoplay = state.autoplay ? state.autoplay : config.is_auto_start;

    // Change the volume if the default volume change
    let volume = state.volume !== undefined ? state.volume : config.volume / 100;
    if (state.neverPlayed && state.volume !== config.volume) {
        volume = config.volume / 100;
    }
    let title = config.title ? config.title : config.display_name;

    let translations = state.translations;
    if (!translations) {
        translations = state.locale ? getTranslations(state.locale) : getTranslations(config.language);
    }

    let result = {
        author: config.artist,
        listenerHold: config.listeners_hold,
        autoplay: autoplay,
        backgroundColor: "#" + config.background_color,
        buttonColor: "#" + config.button_color,
        cover: config.is_cover,
        defaultVolume: config.volume / 100,
        displayListener: config.is_listeners,
        embedEnable: config.is_embed,
        historyHold: config.history_hold ?? 20,
        facebook: config.facebook_link,
        is_metadata_display: Boolean(config.is_metadata_display),
        is_station_thumbnail: Boolean(config.is_station_thumbnail),
        isHlsBuffered: Boolean(config.is_hls_buffered),
        isDirectControl: Boolean(config.is_direct_control),
        historyDisplay: config.history_display ?? 'none',
        isHistoryClickable: Boolean(config.is_history_clickable),
        loadingUrl: switchTranslationFile(config.loading ?? `${AppConfig.storage}defaultWait.svg`, locale),
        locale: locale,
        logo: config.is_logo_enable,
        numberListener: config.listeners || 1,
        playername: config.display_name,
        secondaryColor: "#" + config.secondary_color,
        isHistoryTimeDisplay: Boolean(config.is_history_time_display),
        spectrum: config.is_spectrum,
        stations: config.stations,
        title: title,
        thumbnail: config.thumbnail ?? `${AppConfig.storage}defaultImage.svg`,
        translations: translations,
        twitter: config.twitter_backlink,
        twitterAccount: config.twitter_account,
        twitterHashtags: config.twitter_hashtag ? config.twitter_hashtag.split(',') : null,
        unavailableUrl: switchTranslationFile(config.unavailable ?? `${AppConfig.storage}defaultUnavailable.svg`, locale),
        unauthorizedUrl: switchTranslationFile(config.restriction ?? `${AppConfig.storage}defaultGeoRestriction.svg`, locale),
        url: config.url ?? state.url,
        uuid: config.uuid,
        volume: volume,
        splitHistoryRatio: config?.splitHistoryRatio ?? state?.splitHistoryRatio,
    }

    // Avoid undefined date in preview
    if (config.date) {
        result.date = config.date;
    }

    if (state.neverPlayed) {
        result.display = getPlayerImage(result);
    }

    if (!config.stations || config.stations.length === 0) {
        let defaultConfig = defineSpecificUrl('temp.mp3');
        config.stations = defaultConfig.stations;
    }

    config.stations.forEach(function (station, index) {
        if (!station.streams || station.streams.length === 0) {
            config.stations[index].streams = [{
                "is_main": true,
                "type": 'ice',
                "uuid": -1,
                "url": 'temp.mp3',
                "name": config.title,
                "format": 'mp3',
                "bitrate": -1
            }];
        }
    })

    if (!isPreview) {
        Object.assign(result, getStreamFromConfig(config, state));
    }

    // init the previous playTime if it is not defined
    if (!state.previousPlayTime && result.fragment && result.playlist) {
        const liveOffset = result.fragment * numberFragmentBeforeLive;
        const bufferDuration = result.fragment * result.playlist;

        result.previousPlayTime = result.isHlsBuffered ? (liveOffset - bufferDuration) : (-liveOffset);
    }

    return result;
}

/**
 * Return a base url without any query param
 * @param url
 * @returns {string}
 */
export const getBaseUrl = (url) => {
    try {
        const baseUrl = new URL(url);
        return baseUrl.origin + baseUrl.pathname;
    } catch (e) {
        return url;
    }
}

export var defineSpecificUrl = function (url, config = {}) {
    const baseUrl = getBaseUrl(url);
    let mountpoint = baseUrl.split('/').pop().split('?').shift();
    let name = mountpoint;
    let extension = mountpoint.split('.').pop();
    if (extension === 'm3u8' && mountpoint === "manifest") {
        name = baseUrl.split('/')[baseUrl.split('/').length - 2];
    }
    config.title = name;
    config.display_name = name;
    config.artist = '';
    config.is_cover = false;
    config.is_spectrum = false;
    config.stations = [{
        "id": 1,
        "order": 1,
        "thumbnail": null,
        "streams": [{
            "is_main": true,
            "type": extension === 'm3u8' ? 'hls' : 'ice',
            "uuid": -1,
            "url": url,
            "name": name,
            "format": extension === 'm3u8' ? 'hls' : extension,
            "bitrate": -1
        }]
    }];
    return config;
}

export function getStreamFromConfig(config, state) {
    let result = {streams: [], stream: null};
    // Sometime tokens is undefined for no reason
    let tokens = state.tokens ?? '';

    const streams = config.stations[state.stationIndex].streams.map((stream) => ({
        ...stream,
        url: addUserAgentToStreamUrl(stream.url),
    }));

    // Max 3 streams available
    for (let i = 0; i < 3 && i < streams.length; i++) {
        let stream = streams[i];
        let streamURL = new URL(stream.url);

        let currentLocation = new URL(window.location)
        currentLocation.searchParams.forEach((value, name) => streamURL.searchParams.append(name, value))

        if (tokens[stream.name]) {
            streamURL.searchParams.append('token', tokens[stream.name])
        }

        let newStream = {
            uuid: stream.uuid,
            url: streamURL.toString(),
            name: stream.name
        };

        if (stream.type === 'hls') {
            newStream.format = stream.type;
            result.fragment = stream.fragment_size;
            result.playlist = stream.playlist_size;
            if (stream.bitrates) {
                result.bitrates = stream.bitrates.split(',');
            }
        } else {
            newStream.bitrate = stream.bitrate;
            newStream.format = stream.format;
        }

        // Define main stream
        if (stream.is_main) {
            result.main = newStream;
        }
        result.streams.push(newStream);
    }
    if (!result.main) {
        result.main = result.streams[0];
    }
    result.stream = result.main;
    return result;
}

export function isTemplateConfig(configUrl) {
    return Boolean(configUrl.match(/\/config-.*\.json$/));
}

function switchTranslationFile(file, locale) {
    const availableLang = ['en', 'fr', 'es', 'de', 'it', 'nl', 'pt', 'fi', 'sv', 'da', 'nb', 'el', 'pl'];
    const split = file.split('.');
    const fileLang = split[split.length - 2];

    if (fileLang?.length === 2 && availableLang.includes(fileLang)) {
        split[split.length - 2] = locale;
        return split.join('.');
    } else {
        return file;
    }
}

function getMountpointFromStream(stream) {
    if (stream.format === 'hls') {
        return stream.url.split('/')[stream.url.split('/').length - 2] + ".hls";
    } else {
        return stream.url.split('?').shift().split('/').pop();
    }
}

export function getPlayerImage(state, currentTrack) {
    if (state.cover && currentTrack?.cover) {
        return currentTrack.cover;
    } else if (state.is_station_thumbnail && state.stations.length >= 1) {
        return state.stations[state.stationIndex ?? 0].thumbnail;
    } else if (state.thumbnail) {
        return state.thumbnail;
    } else {
        return AppConfig.storage + 'defaultImage.svg';
    }
}
